import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import {
  Autocomplete,
  ButtonGroup,
  Checkbox,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { FieldArray, Form, Formik, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import { Close } from "@mui/icons-material";
import { useGetAllItemsQuery, usePartnersQuery, useUpdateItemsMutation } from "../../Slices/roomNateApis";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { baseUrl } from "../../utils/baseUrl";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { useGetPartnerZonesQuery } from "../../Slices/roomNateApis";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "90%",
  overflowY: "auto",
  width: { xs: "90%", md: "650px" },
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

export default function BasicModal({
  zonesData: zonesAPIData,
  connectionPointsData,
  connectionPointsOptionsData,
  items,
  open , 
  handleClose
}) {


  const [formValue, setFormValue] = React.useState(null)
  const { currentUserSessionStorage  } = useGlobalAuthContext();

  const { data: itemsO } = useGetAllItemsQuery({itemType:1 , activePartner: formValue?.partners});
  const { data: fetchAllItems , isLoading:itemsLoading } = useGetAllItemsQuery({itemType:1});
    
  const FormikWatcher = ({ onValuesChange }) => {
    const { values } = useFormikContext();
  
    React.useEffect(() => {
      onValuesChange(values);
    }, [values, onValuesChange]);
  
    return null;
  };



  const [updateItem, resultStats] = useUpdateItemsMutation();
  const {data: partnersData } = usePartnersQuery();

  const formRef = React.useRef();

  const [load, setLoad] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);
  const fieldRefs = React.useRef({});

  const [frontImage, setFrontImage] = React.useState("");
  const [topImage, setTopImage] = React.useState("");


  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const { data: partnerZones, error } = useGetPartnerZonesQuery(formValue?.partners, {
    skip: !Array.isArray(formValue?.partners),
  })

  const zonesData = React.useMemo(() => error?.status ? zonesAPIData: partnerZones && partnerZones?.length >= 1 ? partnerZones.filter((zone) => zone?.value != "custom") : zonesAPIData, [error?.status, partnerZones, zonesAPIData]);


  const filteredZones = React.useMemo(() => 
    zonesData?.filter((number) =>
      items?.zones.includes(number.value)
    ), [zonesData, items?.zones]);
  

  const filteredClearanceItems = fetchAllItems?.items?.filter((number) =>
    items?.clearanceItems?.map((item) => item._id).includes(number._id)
  );


  const filteredLayoutItems = fetchAllItems?.items
    ?.filter((number) => {
      const matchingItem = items?.layoutItems?.find(
        (item) => item._id === number._id
      );
      return matchingItem && matchingItem.capacity;
    })
    .map((filteredNumber) => {
      const matchingItem = items?.layoutItems?.find(
        (item) => item._id === filteredNumber._id
      );
      return {
        ...filteredNumber,
        capacity: matchingItem?.capacity || 1, 
      };
  });

  React.useEffect(() => {
    if (filteredZones && formRef.current) {
      formRef.current.setValues(prev => ({
        ...prev,
        zoneValue: filteredZones,
      }));
    }
  }, [filteredZones]);
  
  const validationSchema = Yup.object().shape({
    frontViewImage: Yup.mixed().required("File is required"),
    topViewImage: Yup.mixed().required("File is required"),
    name: Yup.string().required("No name provided"),
    zoneValue: Yup.array().min(1, "Atleast Select one"),
    variants: Yup.array().of(
      Yup.object().shape({
        length: Yup.number()
          .required("No length is provided")
          .min(1, "Length should be greater than 0"),
        name: Yup.string().required("Name is required"),
        priority: Yup.number().required("Priority is required"),
        width: Yup.number()
          .required("No width is provided")
          .min(1, "Width should be greater than 0"),
      })
    ),
  });

  React.useEffect(() => {

    if (resultStats?.isLoading) {
      setLoad(true);
    }

    if (!resultStats?.isLoading) {
      setLoad(false);
    }

    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
    }

    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [resultStats]);

  React.useEffect(() => {
    if (frontImage !== "") {
      if (frontImage.size >= 1e7) {
        setFrontImage("");
      } else {
        const fd = new FormData();
        fd.append("image", frontImage);
        axios
          .post("/items/image-upload/", fd, {
            headers: {
              Authorization: `Bearer ${currentUserSessionStorage}`,
            },
          })
          .then((resp) => {
            formRef?.current?.setValues({
              ...formRef?.current?.values,
              frontViewImage: resp?.data?.path,
            });
          });
      }
    }
  }, [frontImage, currentUserSessionStorage]);

  React.useEffect(() => {
    if (topImage !== "") {
      if (topImage.size >= 1e7) {
        setTopImage("");
      } else {
        const fd = new FormData();
        fd.append("image", topImage);
        axios
          .post("/items/image-upload/", fd, {
            headers: {
              Authorization: `Bearer ${currentUserSessionStorage}`,
            },
          })
          .then((resp) => {
            formRef?.current?.setValues({
              ...formRef?.current?.values,
              topViewImage: resp?.data?.path,
            });
          });
      }
    }
  }, [topImage, currentUserSessionStorage]);

  const updateQty = (itemId, qty) => {
    const updatedItems = formRef?.current?.values.items.map((item) => {
      if (item._id === itemId) {
        return {
          ...item,
          qty,
          capacity: qty, 
        };
      } else {
        return item;
      }
    });

    formRef?.current?.setValues((prevValues) => ({
      ...prevValues,
      items: updatedItems,
    }));
  };

  const handleInc = (itemId) => {
    const item = formRef?.current?.values.items.find(
      (item) => item._id == itemId
    );

    if (item) {
      const newQty = item.capacity + 1;
      updateQty(itemId, newQty);
    }
  };

  const handleDec = (itemId) => {
    const item = formRef?.current?.values.items.find(
      (item) => item._id == itemId
    );

    if (item && item.capacity > 1) {
      const newQty = item.capacity - 1;
      updateQty(itemId, newQty);
    }
  };

  const scrollToError = (errors) => {
    const firstErrorKey = Object.keys(errors)[0]; 

    if (firstErrorKey !== "variants") {
      if (fieldRefs.current[firstErrorKey]) {
        fieldRefs.current[firstErrorKey].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    } else {
      const firstVariantIndex = Object.keys(errors.variants)[0]; 

      if (firstVariantIndex !== undefined) {
        const firstFieldKey = Object.keys(
          errors.variants[firstVariantIndex]
        )[0]; 
        const refKey = `variants.[${firstVariantIndex}].${firstFieldKey}`;

        if (fieldRefs.current[refKey]) {
          fieldRefs.current[refKey].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  };

  return (
    <>
      {successAlert && (
        <SuccessAlert title="Item has been updated successfully!" />
      )}

      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

     

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        {itemsLoading? <p>Loading...</p> :<Box sx={style}>
          <Box position="fixed" top={10} right={0}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
              <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={{
              frontViewImage: items?.frontViewImage,
              topViewImage: items?.topViewImage,
              name: items?.name,
              zoneValue: filteredZones,
              clearanceItems: filteredClearanceItems,
              likelihood: items?.likelihood,
              items: filteredLayoutItems,
              circularPoints: {
                north: items?.circularPoints.north,
                south: items?.circularPoints.south,
                east: items?.circularPoints.east,
                west: items?.circularPoints.west,
                northwest: items?.circularPoints.northwest,
                northeast: items?.circularPoints.northeast,
                southeast: items?.circularPoints.southeast,
                southwest: items?.circularPoints.southwest,
                center: items?.circularPoints.center,
                top: items?.circularPoints.top,
                bottom: items?.circularPoints.bottom,
                cce: items?.circularPoints.cce,
                ccw: items?.circularPoints.ccw,
              },
              wallAttachments: {
                north: items?.wallAttachments.north,
                south: items?.wallAttachments.south,
                east: items?.wallAttachments.east,
                west: items?.wallAttachments.west,
                northwest: items?.wallAttachments.northwest,
                northeast: items?.wallAttachments.northeast,
                southeast: items?.wallAttachments.southeast,
                southwest: items?.wallAttachments.southwest,
                center: items?.wallAttachments.center,
                top: items?.wallAttachments.top,
                bottom: items?.wallAttachments.bottom,
                cce: items?.wallAttachments.cce,
                ccw: items?.wallAttachments.ccw,
              },
              floorAttachments: {
                north: items?.floorAttachments.north,
                south: items?.floorAttachments.south,
                east: items?.floorAttachments.east,
                west: items?.floorAttachments.west,
                northwest: items?.floorAttachments.northwest,
                northeast: items?.floorAttachments.northeast,
                southeast: items?.floorAttachments.southeast,
                southwest: items?.floorAttachments.southwest,
                center: items?.floorAttachments.center,
                top: items?.floorAttachments.top,
                bottom: items?.floorAttachments.bottom,
                cce: items?.floorAttachments.cce,
                ccw: items?.floorAttachments.ccw,
              },
              variants: items?.variants,
              subdomains: items?.subdomains || [],
              partners: items?.partners || []
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              let data;
              const {
                frontViewImage,
                topViewImage,
                name,
                zoneValue,
                variants,
                circularPoints,
                wallAttachments,
                floorAttachments,
                likelihood,
                clearanceItems,
                items: layoutItems,
                subdomains,
                partners
              } = values;

              const newClearanceItems = clearanceItems.map((item) => ({
                name: item.name,
                _id: item._id,
              }));

              let zones = zoneValue.map((item, index) => item.value);

              const layoutItemsChange = layoutItems.map(
                ({ _id, capacity }) => ({ _id, capacity: capacity || 1 })
              );

              data = {
                zones,
                frontViewImage,
                topViewImage,
                name,
                variants,
                circularPoints,
                wallAttachments,
                floorAttachments,
                layoutItems: layoutItemsChange,
                likelihood: likelihood || 10,
                clearanceItems: newClearanceItems,
                subdomains,
                partners: partners
              };

              updateItem({ id: items._id, data }).then((fullfilled)=>{
                if(!fullfilled?.error){
                  handleClose()
                }
              })
            }}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              setValues,
              validateForm,
            }) => (
              <Form noValidate autoComplete="off">
                <FormikWatcher onValuesChange={(vals) => setFormValue(vals)} />
                <Typography
                  id="modal-modal-title"
                  fontWeight={"bold"}
                  fontSize={"20px"}
                  variants="h6"
                  mb={2}
                >
                  Edit an Item
                </Typography>

                <Stack justifyContent={"space-between"} flexDirection="row">
                  <Stack
                    flexDirection={"column-reverse"}
                    ref={(el) => (fieldRefs.current["frontViewImage"] = el)}
                  >
                    {Boolean(
                      getIn(touched, "frontViewImage") &&
                        getIn(errors, "frontViewImage")
                    ) && (
                      <span className="error">{errors?.frontViewImage}</span>
                    )}

                    <Button variant="contained" component="label">
                      Upload
                      <input
                        hidden
                        accept="image/*"
                        name="frontViewImage"
                        type="file"
                        onChange={(e) => setFrontImage(e.target.files[0])}
                      />
                    </Button>

                    <Box>
                      <img
                        src={
                          frontImage
                            ? URL.createObjectURL(frontImage)
                            : `${baseUrl}/g${values?.frontViewImage}`
                        }
                        alt={
                          values?.frontViewImage
                            ? values?.frontViewImage
                            : "placeHolder"
                        }
                        height="100px"
                      />
                    </Box>
                    <Typography fontWeight={"bold"} mb={1}>
                      Front View
                    </Typography>
                  </Stack>

                  <Stack
                    flexDirection={"column-reverse"}
                    ref={(el) => (fieldRefs.current["topViewImage"] = el)}
                  >
                    {Boolean(
                      getIn(touched, "topViewImage") &&
                        getIn(errors, "topViewImage")
                    ) && <span className="error">{errors?.topViewImage}</span>}

                    <Button variant="contained" component="label">
                      Upload
                      <input
                        hidden
                        accept="image/*"
                        type="file"
                        name="topViewImage"
                        onChange={(e) => setTopImage(e.target.files[0])}
                      />
                    </Button>
                    {
                      <Box>
                        <img
                          src={
                            topImage
                              ? URL.createObjectURL(topImage)
                              : `${baseUrl}/g${values?.topViewImage}`
                          }
                          alt={
                            values?.topViewImage
                              ? values?.topViewImage
                              : "placeHolder"
                          }
                          height="100px"
                        />
                      </Box>
                    }
                    <Typography fontWeight={"bold"} mb={1}>
                      Top View
                    </Typography>
                  </Stack>
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Name"
                    variants="outlined"
                    name="name"
                    ref={(el) => (fieldRefs.current["name"] = el)}
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    required
                    helperText={
                      getIn(touched, "name") && getIn(errors, "name")
                        ? getIn(errors, "name")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "name") && getIn(errors, "name")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Likelihood"
                    variants="outlined"
                    name="likelihood"
                    ref={(el) => (fieldRefs.current["likelihood"] = el)}
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.likelihood}
                    required
                    helperText={
                      getIn(touched, "likelihood") &&
                      getIn(errors, "likelihood")
                        ? getIn(errors, "likelihood")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "likelihood") &&
                        getIn(errors, "likelihood")
                    )}
                  />
                </Stack>

                <Stack mt={2}>
                  <Autocomplete
                    onChange={(event, value) => {
                      const uniqueValues = Array?.from(new Set(value?.map((partner) => partner?.value)));
                      setValues({ ...values, partners: uniqueValues });
                    }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    required
                    name="partners"
                    multiple
                    id="checkboxes-tags-partners"
                    options={partnersData?.map((partner) => ({ label: partner?.title, value: partner?._id }))}
                    value={values?.partners?.map(id => {
                      const item = partnersData?.find(obj => obj._id === id);
                      return item ? { label: item.title, value: item._id } : null;
                    }).filter(item => item !== null) || []}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.label}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option._id}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.label}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="partners"
                        label="Partner"
                        ref={(el) => (fieldRefs.current["partners"] = el)}
                        placeholder="Partner"
                        onBlur={handleBlur}
                        helperText={
                          getIn(touched, "partners") &&
                          getIn(errors, "partners")
                            ? getIn(errors, "partners")
                            : ""
                        }
                        error={Boolean(
                          getIn(touched, "partners") &&
                          getIn(errors, "partners")
                        )}
                      />
                    )}
                  />
                </Stack>

                <FieldArray name="variants">
                  {({ push, remove }) => (
                    <div>
                      {values?.variants?.map((p, index) => {
                        const length = `variants.[${index}].length`;
                        const touchedLength = getIn(touched, length);
                        const errorLength = getIn(errors, length);

                        const width = `variants.[${index}].width`;
                        const touchedWidth = getIn(touched, width);
                        const errorWidth = getIn(errors, width);

                        const name = `variants.[${index}].name`;
                        const touchedName = getIn(touched, name);
                        const errorName = getIn(errors, name);

                        const capacity = `variants.[${index}].capacity`;
                        const touchedCapacity = getIn(touched, capacity);
                        const errorCapacity = getIn(errors, capacity);

                        const priority = `variants.[${index}].priority`;
                        const touchedPriority = getIn(touched, priority);
                        const errorPriority = getIn(errors, priority);

                        return (
                          <Box key={index}>
                            <Typography
                              variant="h6"
                              fontWeight={"bold"}
                              fontSize={"20px"}
                            >
                              Variant {index + 1}
                            </Typography>
                            <Grid container spacing={2} mb={2}>
                              <Grid item xs={4}>
                                <TextField
                                  variant="outlined"
                                  label="Name"
                                  name={name}
                                  value={p.name}
                                  ref={(el) => (fieldRefs.current[name] = el)}
                                  required
                                  helperText={
                                    touchedName && errorName ? errorName : ""
                                  }
                                  error={Boolean(touchedName && errorName)}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <TextField
                                  variant="outlined"
                                  label="Capacity"
                                  name={capacity}
                                  ref={(el) =>
                                    (fieldRefs.current[capacity] = el)
                                  }
                                  value={p.capacity}
                                  required
                                  helperText={
                                    touchedCapacity && errorCapacity
                                      ? errorCapacity
                                      : ""
                                  }
                                  error={Boolean(
                                    touchedCapacity && errorCapacity
                                  )}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <TextField
                                  variant="outlined"
                                  label="Priority"
                                  ref={(el) =>
                                    (fieldRefs.current[priority] = el)
                                  }
                                  name={priority}
                                  value={p.priority}
                                  required
                                  helperText={
                                    touchedPriority && errorPriority
                                      ? errorPriority
                                      : ""
                                  }
                                  error={Boolean(
                                    touchedPriority && errorPriority
                                  )}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Grid>
                              <Grid item xs={2}>
                                <Button
                                  type="button"
                                  variant="contained"
                                  onClick={() => remove(index)}
                                >
                                  x
                                </Button>
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  variant="outlined"
                                  label="Length"
                                  ref={(el) => (fieldRefs.current[length] = el)}
                                  name={length}
                                  fullWidth
                                  value={p.length}
                                  required
                                  helperText={
                                    touchedLength && errorLength
                                      ? errorLength
                                      : ""
                                  }
                                  error={Boolean(touchedLength && errorLength)}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  variant="outlined"
                                  label="Width"
                                  ref={(el) => (fieldRefs.current[width] = el)}
                                  fullWidth
                                  name={width}
                                  value={p.width}
                                  required
                                  helperText={
                                    touchedWidth && errorWidth ? errorWidth : ""
                                  }
                                  error={Boolean(touchedWidth && errorWidth)}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      })}
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => {
                          const lastVariant =
                            values?.variants?.[values.variants.length - 1]; 
                          if (lastVariant) {
                            push({
                              ...lastVariant, 
                              capacity: Number(lastVariant.capacity) + 1,
                            });
                          } else {
                            push({
                              name: "",
                              capacity: "",
                              priority: 10,
                              length: "",
                              width: "",
                              spacing: {
                                north: "",
                                east: "",
                                west: "",
                                south: "",
                              },
                            });
                          }
                        }}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                </FieldArray>

                {itemsO?.items  && filteredLayoutItems && (
                  <Stack mt={2}>
                    <Autocomplete
                      onChange={(event, value) =>
                        setValues({
                          ...values,
                          items: value.map((item) => ({ ...item, qty: 1 })),
                        })
                      }
                      required
                      ref={(el) => (fieldRefs.current["items"] = el)}
                      name="items"
                      multiple
                      id="checkboxes-tags-demo"
                      value={values?.items}
                      options={itemsO?.items}
                      isOptionEqualToValue={(option, value) => 
                        option?._id === value?._id
                      }
                      filterOptions={(options, state) => {
                        const uniqueOptions = options.filter(
                          (item, index, self) =>
                            index === self.findIndex((t) => t._id === item._id)
                        );

                        return uniqueOptions.filter((option) =>
                          option.name
                            .toLowerCase()
                            .includes(state.inputValue.toLowerCase())
                        );
                      }}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} key={option._id}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="items"
                          label="Items"
                          placeholder="Select Items"
                          onBlur={handleBlur}
                          helperText={
                            getIn(touched, "items") && getIn(errors, "items")
                              ? getIn(errors, "items")
                              : ""
                          }
                          error={Boolean(
                            getIn(touched, "items") && getIn(errors, "items")
                          )}
                        />
                      )}
                    />
                  </Stack>
                )}

                {values?.items?.length >= 1 && (
                  <Stack
                    justifyContent={"space-between"}
                    flexWrap={"wrap"}
                    flexDirection="column"
                    mt={2}
                  >
                    <Stack
                      flexDirection="row"
                      alignItems="center"
                      justifyContent={"space-between"}
                    >
                      <Typography fontWeight={"bold"}>Name</Typography>
                      <Typography fontWeight={"bold"}>Qty</Typography>
                    </Stack>
                    {values?.items.map((item, index) => {
                      return (
                        <Stack
                          key={index}
                          justifyContent={"space-between"}
                          flexWrap={"wrap"}
                          flexDirection="row"
                          alignItems="center"
                          mt={1}
                        >
                          <Box>
                            <img
                              src={`${baseUrl}/g${item.frontViewImage}`}
                              alt={item.name}
                              height="100px"
                            />
                          </Box>
                          <Stack flexDirection={"row"} alignItems={"center"}>
                            <ButtonGroup
                              size="small"
                              aria-label="small outlined button group"
                              sx={{
                                "& .MuiButtonGroup-firstButton": {
                                  borderRightColor:
                                    "rgba(32, 33, 36, 0.5) !important",
                                },
                              }}
                            >
                              <Button onClick={() => handleDec(item._id)}>
                                -
                              </Button>
                              <Button disabled sx={{ mx: "4px !important" }}>
                                {item.capacity}
                              </Button>
                              <Button onClick={() => handleInc(item._id)}>
                                +
                              </Button>
                            </ButtonGroup>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}

                <Stack mt={2}>
                  <Autocomplete
                    onChange={(_, value) =>
                      setValues({ ...values, zoneValue: value })
                    }
                    required
                    name="zoneValue"
                    multiple
                    id="checkboxes-tags-demo"
                    options={zonesData}
                    value={values?.zoneValue}
                    isOptionEqualToValue={(option, value) =>
                      option?.value === value?.value
                    }
                    disableCloseOnSelect
                    getOptionLabel={(option) => option?.partnerDetails?.title ? option.label + " - " + option.partnerDetails?.title : option.label}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option._id || option.value}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option?.partnerDetails?.title ? option.label + " - " + option.partnerDetails?.title :  option.label }
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="zoneValue"
                        label="Zone"
                        ref={(el) => (fieldRefs.current["zoneValue"] = el)}
                        placeholder="Zone"
                        onBlur={handleBlur}
                        helperText={
                          getIn(touched, "zoneValue") &&
                          getIn(errors, "zoneValue")
                            ? getIn(errors, "zoneValue")
                            : ""
                        }
                        error={Boolean(
                          getIn(touched, "zoneValue") &&
                            getIn(errors, "zoneValue")
                        )}
                      />
                    )}
                  />
                </Stack>

                <Box mt={2}>
                  <Typography
                    component="span"
                    fontWeight={"bold"}
                    fontSize={"20px"}
                  >
                    Wall Attachment Points
                  </Typography>
                  <ListItem
                    sx={{ flexDirection: "column", alignItems: "flex-start" }}
                  >
                    {connectionPointsData?.map((item, index) => {
                      return (
                        <Stack
                          key={index}
                          my={1}
                          flexDirection={
                            item.label ===
                            "Center point between Center and East"
                              ? "column"
                              : item.label ===
                                "Center point between Center and West"
                              ? "column"
                              : "row"
                          }
                          width="100%"
                          alignItems={
                            item.label ===
                            "Center point between Center and East"
                              ? "flex-start"
                              : item.label ===
                                "Center point between Center and West"
                              ? "flex-start"
                              : "center"
                          }
                        >
                          <ListItemText
                            key={index}
                            primaryTypographyProps={{
                              style: {
                                fontWeight: "bold",
                                minWidth: "130px",
                                marginRight: "10px",
                              },
                            }}
                            primary={item.label}
                          />

                          <Autocomplete
                            fullWidth
                            disablePortal
                            onChange={(_, value) =>
                              setValues({
                                ...values,
                                wallAttachments: {
                                  ...values?.wallAttachments,
                                  [item.value]:
                                    ((value || value == "") && value.value) ||
                                    7,
                                },
                              })
                            }
                            value={connectionPointsOptionsData?.find(
                              (item2) =>
                                item2.value ===
                                values?.wallAttachments?.[item.value]
                            )}
                            name={`wallAttachments`}
                            id="combo-box-demo"
                            options={connectionPointsOptionsData}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                              <TextField
                                placeholder={"Select " + item.label + " Points"}
                                name={`wallAttachments`}
                                {...params}
                                label={item.label + " Points"}
                                onBlur={handleBlur}
                                helperText={
                                  getIn(touched, "wallAttachments") &&
                                  getIn(errors, "wallAttachments")
                                    ? getIn(errors, "wallAttachments")
                                    : ""
                                }
                                error={Boolean(
                                  getIn(touched, "wallAttachments") &&
                                    getIn(errors, "wallAttachments")
                                )}
                              />
                            )}
                          />
                        </Stack>
                      );
                    })}
                  </ListItem>
                </Box>

                

            
                <Stack mt={2}>
                  <LoadingButton
                    loading={load}
                    variant="contained"
                    fullWidth
                    type="submit"
                    onClick={() => {
                      validateForm().then((errors) => {
                        if (Object.keys(errors).length > 0) {
                          scrollToError(errors);
                        }
                      });
                    }}
                  >
                    Add
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </Box>}
      </Modal>
    </>
  );
}
